import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import {
  minValue,
  required,
  useGetIdentity,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { DateTimeInput } from "@/components/admin";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { useIsAdmin } from "../sales/useIsAdmin";
import type { LabeledValue, Sale, Task } from "../types";
import { TaskTargetInput } from "./TaskTargetInput";

const saleOptionText = (sale: Sale) => `${sale.first_name} ${sale.last_name}`;

export const TaskFormContent = ({
  selectContact,
}: {
  /*
    How a caller says "the target is NOT implied by my own page". A contact's
    own page leaves it off and keeps that contact as the target; every other
    caller -- the task list, and both edit surfaces -- turns it on so the user
    picks what the task is about.
  */
  selectContact?: boolean;
}) => {
  const { taskOutcomes, taskTypes } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const { isAdmin } = useIsAdmin();
  const record = useRecordContext<Task>();
  const translate = useTranslate();

  /*
    An administrator can hand a task to anyone. A non-administrator gets the
    input only to CLAIM an existing unassigned task, and then the choices are
    restricted to themselves -- on create there is no record to gate on, so an
    ungated input would let anyone put work in another manager's queue.
  */
  const isClaimingUnassigned =
    !isAdmin && record != null && record.sales_id == null;
  const canChooseOwner = isAdmin || isClaimingUnassigned;

  // The configured outcomes are the source of truth for the choices; the
  // catalog only supplies the wording, falling back to the configured label
  // for an outcome a deployment added itself.
  const outcomeOptionText = (choice: LabeledValue) =>
    translate(`resources.tasks.outcome.${choice.value}`, { _: choice.label });

  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="text"
        validate={required()}
        multiline
        className="m-0"
        helperText={false}
      />
      {selectContact && <TaskTargetInput />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DateTimeInput
          source="due_date"
          helperText={false}
          validate={required()}
        />
        <SelectInput
          source="type"
          validate={required()}
          choices={taskTypes}
          optionText="label"
          optionValue="value"
          defaultValue="none"
          helperText={false}
        />
      </div>

      {canChooseOwner && (
        <ReferenceInput
          source="sales_id"
          reference="sales"
          // A claimer may only pick themselves, so the query cannot return
          // anybody else to pick. An administrator sees every manager.
          filter={isAdmin ? undefined : { id: identity?.id }}
        >
          <AutocompleteInput
            label="resources.tasks.fields.sales_id"
            optionText={saleOptionText}
            defaultValue={identity?.id}
            validate={required()}
            helperText={false}
            modal
          />
        </ReferenceInput>
      )}

      {/* Cadence: how the touch went and which touch it was. Secondary to what
          the task is and when it is due, so it sits below them. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectInput
          source="outcome"
          choices={taskOutcomes}
          optionText={outcomeOptionText}
          optionValue="value"
          helperText={false}
        />
        <NumberInput
          source="attempt_number"
          min={1}
          step={1}
          defaultValue={1}
          validate={minValue(1)}
          helperText={false}
        />
      </div>
    </div>
  );
};
